import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Inline-editable text section with a pen icon trigger.
 *
 * For non-admin users: renders `children` (the read-only display) unchanged.
 * For admins: floats a pen button in the top-right corner of the wrapping
 * block. Click → swaps the display for a textarea/input with Save/Cancel.
 * If `isOverridden` is true, also shows a "Revert" link that calls
 * `onRevert` (which the parent maps to clearing this field's override).
 *
 * The component is dumb about *where* the data is stored — it just calls
 * `onSave(newValue)` and lets the parent persist. Errors thrown by the
 * save callback are surfaced inline.
 */
interface EditableFieldProps {
  value: string;
  /** Short label shown above the textarea when editing (e.g. "Description"). */
  label: string;
  /** Read-only render of the current value. */
  children: ReactNode;
  /** True when the user is an admin and the pen should be visible. */
  canEdit: boolean;
  /** True when the value differs from the baseline (override present). */
  isOverridden: boolean;
  multiline: boolean;
  /** Called with the new string when the user clicks Save. */
  onSave: (next: string) => Promise<void>;
  /** Called when the user clicks Revert. Should clear the override. */
  onRevert?: () => Promise<void>;
}

export function EditableField({
  value,
  label,
  children,
  canEdit,
  isOverridden,
  multiline,
  onSave,
  onRevert,
}: EditableFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Esc cancels.
  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setEditing(false);
        setError(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing]);

  // Focus the textarea/input when we open the editor.
  useEffect(() => {
    if (!editing) return;
    const el = editorRef.current?.querySelector<HTMLElement>('textarea, input');
    el?.focus();
  }, [editing]);

  if (!canEdit && !editing) {
    return <>{children}</>;
  }

  const handleSave = async () => {
    if (saving) return;
    if (draft === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    if (!onRevert || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onRevert();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Revert failed');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div
        ref={editorRef}
        style={{
          position: 'relative',
          padding: 'var(--space-2)',
          background: 'var(--surface)',
          border: '1px solid var(--primary)',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        <div
          style={{
            fontSize: '0.7rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            fontWeight: 600,
            marginBottom: 'var(--space-1)',
          }}
        >
          Editing {label}
        </div>
        {multiline ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.min(12, Math.max(3, draft.split('\n').length + 1))}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: '0.9rem',
              fontFamily: 'inherit',
              color: 'var(--text)',
              background: 'var(--surface-raised)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              resize: 'vertical',
              lineHeight: 1.5,
            }}
          />
        ) : (
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: '0.95rem',
              fontFamily: 'inherit',
              color: 'var(--text)',
              background: 'var(--surface-raised)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
            }}
          />
        )}
        {error ? (
          <p
            role="alert"
            style={{
              fontSize: '0.8rem',
              color: '#c0392b',
              marginTop: 'var(--space-1)',
            }}
          >
            {error}
          </p>
        ) : null}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-1)',
            marginTop: 'var(--space-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setError(null);
            }}
            disabled={saving}
            style={btnStyle('ghost')}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || draft === value}
            data-testid={`editable-save-${label.toLowerCase().replace(/\s+/g, '-')}`}
            style={btnStyle('primary')}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  // Display mode with a floating pen for admins.
  return (
    <div style={{ position: 'relative' }}>
      {children}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          gap: 'var(--space-1)',
          alignItems: 'center',
        }}
      >
        {isOverridden && onRevert ? (
          <button
            type="button"
            onClick={handleRevert}
            disabled={saving}
            title="Revert to original"
            style={{
              ...btnStyle('ghost'),
              padding: '2px 6px',
              fontSize: '0.7rem',
            }}
          >
            Revert
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setDraft(value);
            setEditing(true);
            setError(null);
          }}
          title={`Edit ${label.toLowerCase()}`}
          aria-label={`Edit ${label.toLowerCase()}`}
          style={{
            width: 24,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            background: 'var(--surface)',
            color: 'var(--text-muted)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
        >
          <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </button>
      </div>
      {isOverridden ? (
        <span
          aria-hidden="true"
          title="Edited"
          style={{
            position: 'absolute',
            top: -4,
            left: -8,
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--accent)',
          }}
        />
      ) : null}
    </div>
  );
}

function btnStyle(kind: 'primary' | 'ghost'): React.CSSProperties {
  if (kind === 'primary') {
    return {
      padding: '6px 12px',
      fontSize: '0.8rem',
      fontWeight: 700,
      background: 'var(--primary)',
      color: '#fffdf2',
      border: '1px solid var(--primary)',
      borderRadius: 'var(--radius-sm)',
      cursor: 'pointer',
    };
  }
  return {
    padding: '6px 12px',
    fontSize: '0.8rem',
    fontWeight: 600,
    background: 'transparent',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
  };
}
